import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CreditLackModal from "~/components/ui/creditLackModal";
import { DOCUMENT_TYPES_LIST, type DocumentType } from "~/interface/document";
import { GenerateConfirmModal } from "./components/GenerateConfirmModal";
import { SectionGroup } from "./components/SectionGroup";
import { GoalBanner } from "./GoalBanner";
import { useDocumentGeneration } from "./hooks/useDocumentGeneration";
import { usePageViewData } from "./hooks/usePageViewData";
import styles from "./index.module.scss";
import { AppDesignSection } from "./sections/AppDesignSection";
import { CustomizeSpecSection } from "./sections/CustomizeSpecSection";
import { FieldDesignSection } from "./sections/FieldDesignSection";
import { FunctionalRequirementsSection } from "./sections/FunctionalRequirementsSection";
import { SystemOverviewSection } from "./sections/SystemOverviewSection";
import { WorkflowSection } from "./sections/WorkflowSection";

type PageViewProps = {
  organizationId: string;
  projectId: string;
  onOpenSummaryModal: () => void;
};

export const PageView = ({ organizationId, projectId, onOpenSummaryModal }: PageViewProps) => {
  const [confirmTarget, setConfirmTarget] = useState<DocumentType | null>(null);
  const navigate = useNavigate();
  const {
    sections,
    needsCustomize,
    isAllComplete,
    hasSummary,
    isSummaryGenerating,
    staleCount,
    outputs,
  } = usePageViewData();
  const { generateDocument, isCreditLackModalOpen, setIsCreditLackModalOpen, lackCredit } =
    useDocumentGeneration();

  const handleNavigateAppDev = () => {
    navigate(`/${organizationId}/project/${projectId}/app-generation`);
  };

  const getCanRegenerate = (docType: string) => {
    return sections.find((s) => s.documentType === docType)?.canRegenerate ?? false;
  };

  const handleGenerate = (documentType: string) => {
    if (!getCanRegenerate(documentType)) return;
    if (DOCUMENT_TYPES_LIST.includes(documentType as DocumentType)) {
      setConfirmTarget(documentType as DocumentType);
    }
  };

  const handleConfirmGenerate = () => {
    if (confirmTarget) generateDocument(confirmTarget);
    setConfirmTarget(null);
  };

  const getStatus = (docType: string) => {
    return sections.find((s) => s.documentType === docType)?.status ?? "locked";
  };

  const getDetailPath = (docType: string) => {
    const docId = outputs[docType]?.id ?? "";
    return `/${organizationId}/project/${projectId}/${docType}/${docId}`;
  };

  const spStatus = getStatus("system-proposal");
  const showWorkflow = spStatus === "generated" || spStatus === "stale";

  const systemOverviewNumber = 1;
  const workflowNumber = 2;
  const workflowOffset = showWorkflow ? 1 : 0;
  const funcReqNumber = 2 + workflowOffset;
  const appDesignNumber = 3 + workflowOffset;
  const fieldDesignNumber = 4 + workflowOffset;
  const customizeSpecNumber = 5 + workflowOffset;

  return (
    <div className={styles["pageView"]} data-testid="page-view">
      <GoalBanner
        hasSummary={hasSummary}
        isSummaryGenerating={isSummaryGenerating}
        isAllComplete={isAllComplete}
        hasStale={staleCount > 0}
        sections={sections}
        onUploadClick={onOpenSummaryModal}
        onNavigateAppDev={handleNavigateAppDev}
      />

      <div className={styles["sections"]}>
        <SectionGroup>
          <SystemOverviewSection
            number={systemOverviewNumber}
            status={getStatus("system-proposal")}
            detailPath={getDetailPath("system-proposal")}
            onGenerate={handleGenerate}
            canRegenerate={getCanRegenerate("system-proposal")}
          />
          <WorkflowSection number={workflowNumber} status={getStatus("system-proposal")} />
        </SectionGroup>
        <SectionGroup>
          <FunctionalRequirementsSection
            number={funcReqNumber}
            status={getStatus("functional-requirements")}
            detailPath={getDetailPath("functional-requirements")}
            onGenerate={handleGenerate}
            canRegenerate={getCanRegenerate("functional-requirements")}
          />
        </SectionGroup>
        <SectionGroup>
          <AppDesignSection
            number={appDesignNumber}
            status={getStatus("app-design")}
            detailPath={getDetailPath("app-design")}
            onGenerate={handleGenerate}
            canRegenerate={getCanRegenerate("app-design")}
          />
        </SectionGroup>
        <SectionGroup>
          <FieldDesignSection
            number={fieldDesignNumber}
            status={getStatus("field-design")}
            detailPath={getDetailPath("field-design")}
            onGenerate={handleGenerate}
            canRegenerate={getCanRegenerate("field-design")}
          />
        </SectionGroup>
        {needsCustomize && (
          <SectionGroup>
            <CustomizeSpecSection
              number={customizeSpecNumber}
              status={getStatus("customize-specification")}
              detailPath={getDetailPath("customize-specification")}
              onGenerate={handleGenerate}
              canRegenerate={getCanRegenerate("customize-specification")}
            />
          </SectionGroup>
        )}
        <div className={styles["appDevLink"]}>
          {(() => {
            const isAppDevButtonDisabled = !isAllComplete || staleCount > 0;
            const appDevButtonTitle = !isAllComplete
              ? "すべてのドキュメント生成を完了してください"
              : staleCount > 0
                ? "更新が必要なドキュメントがあります"
                : undefined;
            return (
              <button
                type="button"
                className={`${styles["appDevButton"]} ${!isAppDevButtonDisabled ? styles["appDevButtonEnabled"] : styles["appDevButtonDisabled"]}`}
                onClick={handleNavigateAppDev}
                disabled={isAppDevButtonDisabled}
                title={appDevButtonTitle}
              >
                アプリ生成へ進む
                <ArrowForwardIcon className={styles["appDevButtonIcon"] ?? ""} />
              </button>
            );
          })()}
        </div>
      </div>
      {isCreditLackModalOpen && (
        <CreditLackModal
          isOpen={isCreditLackModalOpen}
          onClose={() => setIsCreditLackModalOpen(false)}
          lackCredit={lackCredit}
        />
      )}
      {confirmTarget && (
        <GenerateConfirmModal
          isOpen={!!confirmTarget}
          documentType={confirmTarget}
          onConfirm={handleConfirmGenerate}
          onClose={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
};
